/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FieldsSelection,
  createClient,
  QueryGenqlSelection,
  Query,
} from '@rematter/frontend-lib-api-client';
import { DeepOmit } from '@rematter/ui-web';
import {
  QueryObserverResult,
  RefetchOptions,
  UseQueryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import { atom, getDefaultStore, Getter, WritableAtom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import camelCase from 'lodash/camelCase';

import { config } from '~/config';
import { $accessToken } from '~/shared/providers/auth/identity.localState';

const BASE_GRAPHQL_URL = `${config.api.url}/graphql`;
const getAccessToken = () => getDefaultStore().get($accessToken);

export type QueryOptions<TData, TResult> = {
  select?: UseQueryOptions<TData, Error, TResult>['select'];
  staleTime?: number;
  refetchInterval?: number;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  initialData?: TResult;
  alias?: string;
  mock?: TResult;
  keepPreviousData?: boolean;
};

type ParameterizedRefetch<T, TVariables, TResult> =
  T extends WritableAtom<infer U, infer V, infer W>
    ? WritableAtom<
        Omit<U, 'refetch'> & {
          refetch: (
            overrideVariables?: Partial<TVariables>,
          ) => Promise<TResult>;
          fetch: (overrideVariables?: Partial<TVariables>) => Promise<TResult>;
          loadingState: {
            isInitialLoading: boolean;
            isFilterOrRefetch: boolean;
          };
        },
        V,
        W
      >
    : never;

type QueryAtomReturnType<TData, TResult> = ReturnType<
  typeof atomWithQuery<TData, Error, TResult>
>;

export type QueryAtomResult<T extends (...args: any) => void> = NonNullable<
  ReturnType<T> extends WritableAtom<infer U, [], void>
    ? U extends { data: infer D }
      ? NonNullable<D>
      : never
    : never
>;

export type ParameterizedQueryAtom<TVariables, TData, TResult> =
  ParameterizedRefetch<
    QueryAtomReturnType<TData, TResult>,
    TVariables,
    TResult
  >;

export type QueryAtomReadValue<TResult> = Omit<
  QueryObserverResult<TResult, Error>,
  'refetch' | 'data'
> & {
  data: TResult | undefined;
  refetch: (
    overrideVariables?: Partial<unknown>,
  ) => Promise<TResult | undefined>;
  fetch: (overrideVariables?: Partial<unknown>) => Promise<TResult | undefined>;
  loadingState: {
    isInitialLoading: boolean;
    isFilterOrRefetch: boolean;
  };
};

type GetParamsWithoutVariables = (get: Getter) => {
  skip?: boolean;
  lazy?: boolean;
  variables?: never; // to prevent anyone from passing variables
  options?: never; // to prevent anyone from passing options
};

type GetParamsWithVariables<TVariables> = (get: Getter) => {
  variables: TVariables;
  skip?: boolean;
  lazy?: boolean;
  options?: never; // to prevent anyone from passing options
};

export type QueryAtomFactoryReturn<TVariables, TData, TResult> =
  unknown extends TVariables
    ? (
        getParams?: GetParamsWithoutVariables,
      ) => ParameterizedQueryAtom<TVariables, TData, TResult>
    : (
        getParams: GetParamsWithVariables<TVariables>,
      ) => ParameterizedQueryAtom<TVariables, TData, TResult>;

export const createQueryAtomFactory = <
  TSelection extends QueryGenqlSelection,
  TVariables,
  TData = DeepOmit<FieldsSelection<Query, TSelection>, '__typename'>,
  TResult = TData,
>(
  key: `${string}.${string}`, // 'transportation.truckCreate'
  selectionFn: (variables: TVariables) => TSelection,
  {
    select,
    alias,
    mock,
    keepPreviousData: shouldKeepPreviousData = true,
    ...options
  }: QueryOptions<TData, TResult> = {},
): QueryAtomFactoryReturn<TVariables, TData, TResult> => {
  const queryName = `query.${key}`;

  return ((
    getParams?: (get: Getter) => {
      variables: TVariables;
      skip?: boolean;
      lazy?: boolean;
    },
  ) => {
    const tempOverrideVariables: Partial<TVariables>[] = [];

    let refetchRef: (
      options?: RefetchOptions,
    ) => Promise<QueryObserverResult<TResult, Error>>;

    const queryAtom = atomWithQuery((get) => {
      const params = getParams?.(get) ?? {
        skip: false,
        lazy: false,
        variables: {},
      };

      return {
        ...options,
        enabled: !params.skip && !params.lazy,
        queryKey: [queryName, params.variables],
        placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
        queryFn: () => {
          const accessToken = getAccessToken();

          const client = createClient({
            url: `${BASE_GRAPHQL_URL}?q=${queryName}`,
            headers: {
              'Content-Type': 'application/json',
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
            },
            credentials: 'include',
          });

          const variables = {
            ...params.variables,
            // Because until here the execution is synchronous, we can safely pop the last override variables
            // and use them in the async code below
            ...tempOverrideVariables.pop(),
          } as never;

          return (async () => {
            if (mock !== undefined) {
              return mock;
            }

            const result = await client.query({
              ...selectionFn(variables),
              __name: camelCase(alias || key),
            });

            try {
              return select ? select(result as TData) : (result as TResult);
            } catch (err) {
              console.error('Failed to select data.', err);
              throw err;
            }
          })();
        },
      };
    });

    const parameterizedFetch = async (
      overrideVariables?: Partial<TVariables>,
    ) => {
      if (overrideVariables) {
        tempOverrideVariables.push(overrideVariables);
      }

      // Get the query client and force a fresh network request
      const queryClient = getDefaultStore().get(queryClientAtom);

      // Remove the query from cache entirely to force a fresh fetch
      queryClient.removeQueries({ queryKey: [queryName] });

      // Small delay to ensure cache removal is processed
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Force a network request by cancelling any in-flight refetch
      return refetchRef({ cancelRefetch: true }).then((r) => r.data);
    };

    const newAtom = atom((get) => {
      const { refetch, isFetching, isPending, data, ...rest } = get(queryAtom);

      refetchRef = refetch;

      const isInitialLoading = isPending || (isFetching && data === undefined);
      const isFilterOrRefetch = isFetching && data !== undefined;

      return {
        ...rest,
        isFetching,
        isPending,
        data,
        refetch: parameterizedFetch,
        fetch: parameterizedFetch,
        loadingState: {
          isInitialLoading,
          isFilterOrRefetch,
        },
      };
    });

    newAtom.debugLabel = `$query.${key}`;

    return newAtom;
  }) as never;
};
